/**
 * useDeleteTemplateDialog — delete-confirmation state + delete mutation
 * wiring, extracted from BlogTemplatesPage as-is.
 *
 * Page behavior preserved: system templates are blocked client-side with a
 * toast before the mutation; on success the selection is cleared when the
 * deleted template was selected; React Query invalidation refetches the list.
 *
 * Composition note: the selection interaction is injected
 * (`selectedTemplateId` + `onClearSelection`) so the parent can pass
 * useTemplateSelection's state and URL-syncing clearSelection.
 */

import { useCallback, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useDeleteBlogTemplate } from '@/hooks/useBlogTemplates';
import type { BlogTemplateSummary } from '@/services/blogTemplatesService';

export interface UseDeleteTemplateDialogOptions {
  /** Currently selected template id (null when nothing is selected). */
  selectedTemplateId: string | null;
  /** Called when the deleted template was the selected one. */
  onClearSelection: () => void;
}

export interface UseDeleteTemplateDialogResult {
  deleteConfirmTemplate: BlogTemplateSummary | null;
  /** Opens the confirmation dialog for the given template. */
  handleOpenDeleteConfirm: (template: BlogTemplateSummary) => void;
  /** Cancels the confirmation dialog without deleting. */
  handleCancelDelete: () => void;
  /** Confirms and performs the delete; always closes the dialog. */
  handleDeleteTemplate: (template: BlogTemplateSummary) => Promise<void>;
  isDeleting: boolean;
}

export function useDeleteTemplateDialog({
  selectedTemplateId,
  onClearSelection,
}: UseDeleteTemplateDialogOptions): UseDeleteTemplateDialogResult {
  const { toast } = useToast();
  const [deleteConfirmTemplate, setDeleteConfirmTemplate] = useState<BlogTemplateSummary | null>(null);

  // Delete mutation
  const deleteTemplateMutation = useDeleteBlogTemplate();

  // Memoized handler to open delete confirmation dialog
  const handleOpenDeleteConfirm = useCallback((template: BlogTemplateSummary) => {
    setDeleteConfirmTemplate(template);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmTemplate(null);
  }, []);

  const handleDeleteTemplate = async (template: BlogTemplateSummary) => {
    if (template.is_system) {
      toast({
        title: "Cannot delete",
        description: "System templates cannot be deleted",
        variant: "destructive",
      });
      return;
    }

    try {
      await deleteTemplateMutation.mutateAsync(template.id);
      toast({
        title: "Template deleted",
        description: `"${template.name}" has been deleted`,
      });
      // Clear selection if deleted template was selected
      if (selectedTemplateId === template.id) {
        onClearSelection();
      }
      // React Query automatically invalidates and refetches the list
    } catch (err) {
      console.error('Failed to delete template:', err);
      toast({
        title: "Delete failed",
        description: "Failed to delete template. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteConfirmTemplate(null);
    }
  };

  return {
    deleteConfirmTemplate,
    handleOpenDeleteConfirm,
    handleCancelDelete,
    handleDeleteTemplate,
    isDeleting: deleteTemplateMutation.isPending,
  };
}
